import React, { useEffect, useState } from 'react'

import CurrencyInput from './CurrencyInput'
import { sanitizeHTML } from '../../../../shared/utils/common-utils'

let TaxControlTriangleImage = ''
let TaxFreeArrowsImage = ''
let TaxDeferredArrowsImage = ''
let TaxableArrowsImage = ''
let TaxControlTriangleImageAlt = ''
let TaxFreeArrowsImageAlt = ''
let TaxDeferredArrowsImageAlt = ''
let TaxableArrowsImageAlt = ''

/**
 * The TaxControlTriangle component displays a header, subhead, the tax control triangle
 * figure and three areas that describe the different types of assets. Updating
 * your allocated amounts in each of the areas updates the number displayed on the triangle.
 *
 * The inputs to this component are scratchpad inputs and so do not need to be managed
 * outside of this component.
 *
 * @returns a component
 */
const TaxControlFigure = ({
  taxFreeValid,
  taxDeferredValid,
  taxableValid,
  formattedTaxFreeAssets,
  formattedTaxDeferredAssets,
  formattedTaxableAssets,
}) => {
  return (
    <>
      {TaxControlTriangleImage && (
        <img
          className='u-sizeFull'
          src={TaxControlTriangleImage.trim()}
          alt={TaxControlTriangleImageAlt}
        />
      )}
      {taxFreeValid && (
        <img
          className='arrows tax-free'
          src={TaxFreeArrowsImage.trim()}
          alt={TaxFreeArrowsImageAlt}
        />
      )}
      {taxDeferredValid && (
        <img
          className='arrows tax-deferred'
          src={TaxDeferredArrowsImage.trim()}
          alt={TaxDeferredArrowsImageAlt}
        />
      )}
      {taxableValid && (
        <img
          className='arrows taxable'
          src={TaxableArrowsImage.trim()}
          alt={TaxableArrowsImageAlt}
        />
      )}
      {taxFreeValid && (
        <div className='assets tax-free u-textColorBrand'>{formattedTaxFreeAssets}</div>
      )}
      {taxDeferredValid && (
        <div className='assets tax-deferred u-textColorCher'>{formattedTaxDeferredAssets}</div>
      )}
      {taxableValid && (
        <div className='assets taxable u-textColorAdele'>{formattedTaxableAssets}</div>
      )}
    </>
  )
}

const TaxControlTriangle = props => {
  const initialState = {
    taxFreeAssets: '',
    formattedTaxFreeAssets: '',
    taxFreeValid: '',
    taxDeferredAssets: '',
    formattedTaxDeferredAssets: '',
    taxDeferredValid: '',
    taxableAssets: '',
    formattedTaxableAssets: '',
    taxableValid: '',
  }
  const [state, setState] = useState(() => {
    const savedState = localStorage.getItem(`TaxControlTriangle`)
    return savedState ? JSON.parse(savedState) : initialState
  })

  useEffect(() => {
    localStorage.setItem(`TaxControlTriangle`, JSON.stringify(state))
  }, [state])

  const handleStateChange = (name, value) => {
    setState(prev => ({ ...prev, [name]: value }))
  }

  let title
  let description
  let topContent
  let middleContent
  let bottomContent
  let rothiraContent

  title = props.titleContent
  description = props.descriptionContent
  topContent = props.topContent
  middleContent = props.middleContent
  bottomContent = props.bottomContent
  TaxControlTriangleImage = props.imageContentOne
  TaxFreeArrowsImage = props.imageContentTwo
  TaxDeferredArrowsImage = props.imageContentThree
  TaxableArrowsImage = props.imageContentFour
  rothiraContent = props.rothiraContent

  return (
    <div className='u-sizeConstrained u-bgColorWhite u-padding8gu u-pt-paddingVert0'>
      <div className='Grid Grid--alignCenter u-paddingBottom8gu u-pt-paddingVert0'>
        <h2 className='u-lg-sizeFull u-textCenter u-textSecondary u-textColorBrand u-marginBottom5gu'>
          {title}
        </h2>
        {props.descriptionContent && (
          <>
            <div
              className='u-size3of4 tax-control-triangle-description'
              dangerouslySetInnerHTML={{ __html: sanitizeHTML(description) }}></div>
          </>
        )}
      </div>
      <div className='print-force-background Grid Grid--withGutter u-paddingHoriz4gu tax-control-wrapper'>
        <div className='tax-control-triangle__scratchpad u-lg-size1of3 u-sm-sizeFull u-md-sizeFull u-padding0'>
          <div className='u-bgColorBrand Content Content--reversed u-margin0 u-padding4gu'>
            {props.topContent && (
              <>
                <div
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHTML(topContent),
                  }}></div>
              </>
            )}
            <div className='u-marginTop4gu'>
              <label
                htmlFor='tax-free-assets'
                className='u-textSizeMsr Form-label u-textColorWhite'>
                Tax-free assets<span className='u-textColorWhite pt-inline'>:</span>
              </label>
              <CurrencyInput
                printWhiteText={true}
                name='tax-free-assets'
                id='tax-free-assets'
                value={state.taxFreeAssets}
                valueChanged={value => handleStateChange('taxFreeAssets', value)}
                formattedValueChanged={value => handleStateChange('formattedTaxFreeAssets', value)}
                validChanged={value => handleStateChange('taxFreeValid', value)}
              />
            </div>
          </div>
          <div className='u-bgColorBerry Content Content--reversed u-padding4gu'>
            {props.middleContent && (
              <>
                <div
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHTML(middleContent),
                  }}></div>
              </>
            )}
            <div className='u-marginTop4gu'>
              <label
                htmlFor='tax-deferred-assets'
                className='u-textSizeMsr Form-label u-textColorWhite'>
                Tax-Deferred assets<span className='u-textColorWhite pt-inline'>:</span>
              </label>
              <CurrencyInput
                printWhiteText={true}
                name='tax-deferred-assets'
                id='tax-deferred-assets'
                value={state.taxDeferredAssets}
                valueChanged={value => handleStateChange('taxDeferredAssets', value)}
                formattedValueChanged={value =>
                  handleStateChange('formattedTaxDeferredAssets', value)
                }
                validChanged={value => handleStateChange('taxDeferredValid', value)}
              />
            </div>
          </div>
          <div className='u-bgColorDarkMint Content Content--reversed u-padding4gu'>
            {props.bottomContent && (
              <>
                <div
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHTML(bottomContent),
                  }}></div>
              </>
            )}
            <div className='u-marginTop4gu'>
              <label htmlFor='taxable-assets' className='u-textSizeMsr Form-label u-textColorWhite'>
                Taxable assets<span className='u-textColorWhite pt-inline'>:</span>
              </label>
              <CurrencyInput
                printWhiteText={true}
                name='taxable-assets'
                id='taxable-assets'
                value={state.taxableAssets}
                valueChanged={value => handleStateChange('taxableAssets', value)}
                formattedValueChanged={value => handleStateChange('formattedTaxableAssets', value)}
                validChanged={value => handleStateChange('taxableValid', value)}
              />
            </div>
          </div>
        </div>
        <div className='u-flex u-flexCol u-size2of3 u-flexAlignSelfCenter tax-control-triangle-image'>
          <figure className='tax-control-triangle u-posRelative u-sm-sizeFull u-md-sizeFull u-padding8gu u-textSecondary u-textSizeMsrp1'>
            <TaxControlFigure
              taxFreeValid={state.taxFreeValid}
              taxDeferredValid={state.taxDeferredValid}
              taxableValid={state.taxableValid}
              formattedTaxFreeAssets={state.formattedTaxFreeAssets}
              formattedTaxDeferredAssets={state.formattedTaxDeferredAssets}
              formattedTaxableAssets={state.formattedTaxableAssets}
            />
          </figure>
          {rothiraContent && (
            <div
              className='Content u-padding8gu'
              dangerouslySetInnerHTML={{
                __html: sanitizeHTML(rothiraContent),
              }}></div>
          )}
        </div>
      </div>
    </div>
  )
}

export default TaxControlTriangle
